import { httpClient, InvalidApiResponseError } from "@bw/shared/api";
import type { components, paths } from "@bw/shared/types/generated";

export type OrgStatus =
  | "pending"
  | "in_review"
  | "approved"
  | "active"
  | "rejected"
  | "suspended";

// Request body for POST /orgs/register. Sourced from the backend OpenAPI schema
// (OrgRegistration) so the FE owns NO field shape of its own — a backend change
// regenerates this type and CI blocks drift. The backend validates and creates
// the org; the FE only collects + POSTs.
export type OrgRegistrationInput = components["schemas"]["OrgRegistration"];

export type OrgAddress = components["schemas"]["Address"];
export type OwnerOrgResponse = components["schemas"]["OrgResponse"];

// UI-domain organization shape. `/orgs/me` deliberately returns either the
// full owner response or the PII-free staff workspace response. Normalize both
// generated wire shapes into a partial owner shape so shared screens can render
// the common fields without pretending staff received owner-only data.
export type Org = Omit<Partial<OwnerOrgResponse>, "status"> & {
  id?: string;
  name?: string;
  status: OrgStatus;
  can_manage_staff?: MyOrgResponse["can_manage_staff"];
  finance_capability?: MyOrgResponse["finance_capability"];
};

export type OrgWorkspace = components["schemas"]["OrgWorkspaceContextResponse"];
export type MyOrgResponse =
  paths["/orgs/me"]["get"]["responses"][200]["content"]["application/json"];
export type MyOrgNotFoundResponse =
  paths["/orgs/me"]["get"]["responses"][404]["content"]["application/json"];

// Mirrors the backend Address model (Stripe-aligned). country is a 2-letter
// ISO code; line1/city/postal_code are required by the backend when address
// is sent. latitude/longitude are attached for a Google-verified match.
export interface OrgAddressInput {
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postal_code: string;
  country: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface OrgUpdate {
  business_name?: string | null;
  legal_name?: string | null;
  // 9-digit EIN / tax id. Editable post-approval (a correction) even when the
  // business/legal names are locked, so it lives on the update body independently.
  tax_id?: string | null;
  // A VERIFIED HQ address carries coordinates; the panel only PATCHes once the
  // draft is Google-matched, so the full address (with lat/lng) is sent.
  address?: OrgAddressInput;
}

export interface OnboardingStep {
  key: string;
  label: string;
  complete: boolean;
  required: boolean;
  required_for_approval: boolean;
  required_for_go_live: boolean;
  applies_to_services?: string[];
  section_key: string;
  route?: string | null;
  description?: string | null;
}

export interface OnboardingSection {
  key: string;
  label: string;
}

export interface OnboardingAction {
  key: string;
  label: string;
  enabled: boolean;
  reason?: string | null;
}

// Read-only echo of what the owner submitted, surfaced on the rejected screen so
// they can see (and re-confirm) the details that were reviewed. Owned by the
// backend onboarding-state endpoint; the FE only displays it.
export interface OnboardingSubmissionSummary {
  business_name?: string | null;
  country?: string | null;
  owner_email?: string | null;
  owner_full_name?: string | null;
  owner_phone?: string | null;
  submitted_at?: string | null;
}

export interface OnboardingState {
  org_id: string;
  status: OrgStatus;
  display: { view: string; title?: string; subtitle?: string };
  steps: OnboardingStep[];
  sections: OnboardingSection[];
  actions: OnboardingAction[];
  alerts: string[];
  rejection?: { reason?: string; can_resubmit?: boolean } | null;
  submission_summary?: OnboardingSubmissionSummary | null;
  contract_terms?: ContractTerms | null;
}

// Payload for re-submitting a rejected org. The backend re-validates everything
// and resets the org to pending; the FE owns no business logic, it only forwards
// the corrected fields the owner confirmed.
export interface OrgResubmitInput {
  business_name: string;
  legal_name?: string;
  country: string;
  owner_full_name: string;
  owner_email: string;
  owner_phone?: string;
  owner_position?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

// ADR-005: the contract terms shape is the GENERATED ContractTermsResponse —
// the backend Pydantic model is the source of truth (commission_rate is a
// branded RateFraction 0..1 Decimal string; payout_schedule is the NESTED
// generated PayoutSchedule whose min_payout_amount is a branded Money). The
// old hand-written loose mirror (an unbranded optional commission_rate, and
// missing past_grandfather/termination/upcoming_* facts) is retired; a backend
// schema change regenerates this type and CI blocks drift.
export type ContractTerms = components["schemas"]["ContractTermsResponse"];
export type ContractTermsAcceptance =
  components["schemas"]["OrgContractTermsAcceptanceResponse"];
export type ContractTermsAcceptInput =
  components["schemas"]["OrgContractTermsAcceptRequest"];

const ORG_STATUSES: ReadonlySet<string> = new Set<OrgStatus>([
  "pending",
  "in_review",
  "approved",
  "active",
  "rejected",
  "suspended",
]);
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isOrgWorkspaceResponse(value: unknown): value is OrgWorkspace {
  return (
    isRecord(value) &&
    typeof value.org_id === "string" &&
    UUID_PATTERN.test(value.org_id) &&
    typeof value.business_name === "string" &&
    value.business_name.length > 0 &&
    typeof value.status === "string" &&
    ORG_STATUSES.has(value.status) &&
    typeof value.can_manage_staff === "boolean" &&
    ["allowed", "denied", "unavailable"].includes(
      String(value.finance_capability),
    )
  );
}

function normalizeMyOrg(response: MyOrgResponse): Org {
  return {
    ...response,
    status: response.status as OrgStatus,
  };
}

export async function getMyOrg(organizationId?: string | null): Promise<Org> {
  // Selection belongs to the authenticated workspace context, never a
  // browser-global storage value. Omission asks the server to resolve it.
  const selectedOrganizationId = organizationId;
  const query = selectedOrganizationId
    ? `?organization_id=${encodeURIComponent(selectedOrganizationId)}`
    : "";
  const endpoint = `/orgs/me${query}`;
  const response = await httpClient.get<unknown>(endpoint);
  if (!isOrgWorkspaceResponse(response)) {
    throw new InvalidApiResponseError(endpoint);
  }
  return normalizeMyOrg(response);
}

export async function listMyOrgOptions(): Promise<OrgWorkspace[]> {
  const endpoint = "/orgs/me/options";
  const response = await httpClient.get<unknown>(endpoint);
  if (!Array.isArray(response) || !response.every(isOrgWorkspaceResponse)) {
    throw new InvalidApiResponseError(endpoint);
  }
  return response;
}

// Register a new business on the platform. Thin POST wrapper — the backend
// validates the payload, creates the org (status: pending) and returns it, or
// rejects with a 409 (duplicate business) / 4xx (validation). The caller owns
// no business logic; it only forwards the collected fields + accepted legal
// versions.
export function registerOrg(payload: OrgRegistrationInput): Promise<Org> {
  return httpClient.post<Org>("/orgs/register", payload);
}

export function updateOrg(id: string, data: OrgUpdate): Promise<Org> {
  return httpClient.patch<Org>(`/orgs/${id}`, data);
}

export function acceptContractTerms(
  id: string,
  data: ContractTermsAcceptInput,
  idempotencyKey: string,
): Promise<ContractTermsAcceptance> {
  return httpClient.post<ContractTermsAcceptance>(
    `/orgs/${id}/accept-contract-terms`,
    data,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
}

export function withdrawContractTerms(id: string): Promise<void> {
  return httpClient.delete<void>(`/orgs/${id}/accept-contract-terms`);
}

export function submitOrgForReview(id: string): Promise<Org> {
  return httpClient.post<Org>(`/orgs/${id}/submit-review`);
}

export function goLive(id: string): Promise<{ status: string }> {
  return httpClient.post<{ status: string }>(`/orgs/${id}/go-live`);
}

export function getOnboardingState(orgId: string): Promise<OnboardingState> {
  return httpClient.get<OnboardingState>(`/orgs/${orgId}/onboarding-state`);
}

// Re-submit a rejected org for review. Thin POST wrapper — the backend
// re-validates and resets the org to pending. getMyOrg deliberately THROWS on a
// no-org 404; useMyOrg maps only its stable ORG_NOT_FOUND code to `no-org`.
export function resubmitOrg(
  orgId: string,
  payload: OrgResubmitInput,
): Promise<Org> {
  return httpClient.post<Org>(`/orgs/${orgId}/resubmit`, payload);
}

export const orgId = (org: Org) => org.org_id ?? org.id ?? "";
export const orgName = (org: Org) =>
  org.business_name ?? org.name ?? "Your organization";
